import { Component, OnDestroy, OnInit } from '@angular/core';
import { DwEventService, DwSecurityUserService } from '@devwareapps/devware-cap';
import { Subscription } from 'rxjs';
import { CourseRepositoryService } from '../../../courses/services/course-repository.service';
import { StudentCourseEntity } from '../../../../meta-data/app-meta-data.service';
import { IntegrationRepositoryService } from '../../../integration/services/integration-repository.service';
import { SchoolRepositoryService } from '../../../schools/services/school-repository.service';
import { CourseAssignmentOutcomeService } from '../../../students/services/course-assignment-outcome.service';
import { CourseAssignmentOutcome } from '../../../students/models/course-assignment-outcome.model';
import { SchoolFeatureId } from '../../../schools/models/school-features.model';
import { GamificationBootstrap } from '../../../gamification/models/gamification-bootstrap.model';
import { GamificationRepositoryService } from '../../../gamification/services/gamification-repository.service';
import { COURSE_PURCHASED_EVENT } from '../../../payments/services/payments-purchase.service';
import { UnlimitedCourseAccessService } from '../../../courses/services/unlimited-course-access.service';
import { map, mergeMap } from 'rxjs/operators';

@Component({
  selector: 'app-main-dashboard-redesign',
  templateUrl: './main-dashboard-redesign.component.html',
  styleUrls: ['./main-dashboard-redesign.component.scss']
})
export class MainDashboardRedesignComponent implements OnInit, OnDestroy {
  studentCourses: StudentCourseEntity[];
  currentStudentCourse: StudentCourseEntity;
  firstName: string = 'Future Pilot';
  today: Date = new Date();

  courseProgressFilters: any = {};

  showProgress: boolean = false;
  heroSlotLoaded: boolean = false;
  heroLoading: boolean = true;
  streakQuizLoading: boolean = true;
  achievementsLoading: boolean = true;
  programsLoading: boolean = true;
  assignmentsLoading: boolean = true;
  showGamification: boolean = false;
  gamificationBootstrap: GamificationBootstrap;

  programsCount: number = 0;
  assignmentsCount: number = 0;
  showMarketplacePrograms: boolean = false;
  availableCoursesTitle: string = 'Marketplace';
  availableCoursesLinkText: string = 'Browse marketplace';

  private myCoursesCount: number | null = null;
  private marketplaceEnabled: boolean = false;
  private hasUnlimitedCourseAccess: boolean = false;
  private gamificationEnabled: boolean = false;
  private schoolFeaturesLoaded: boolean = false;
  private gamificationLoadStarted: boolean = false;
  private gamificationOverviewResolved: boolean = false;
  private dailyQuizResolved: boolean = false;
  private schoolFeaturesSubscription: Subscription;
  private coursePurchasedSubscription: Subscription;
  private schoolFeaturesFlightSchoolId: number;

  readonly programSkeletonCards = [1, 2, 3];
  readonly assignmentSkeletonCards = [1, 2];
  readonly assignmentSkeletonRows = [1, 2, 3];

  message: any;
  courseAssignmentOutcomes: CourseAssignmentOutcome[] = [];
  courseAssignmentAlertTitle: string;
  courseAssignmentAlertBody: string;
  acknowledgingCourseAssignments = false;

  constructor(
    private dwSecurityUserService: DwSecurityUserService,
    private courseRepositoryService: CourseRepositoryService,
    private integrationRepositoryService: IntegrationRepositoryService,
    private schoolRepositoryService: SchoolRepositoryService,
    private courseAssignmentOutcomeService: CourseAssignmentOutcomeService,
    private gamificationRepositoryService: GamificationRepositoryService,
    private dwEventService: DwEventService,
    private unlimitedCourseAccessService: UnlimitedCourseAccessService,
  ) { }

  ngOnInit(): void {
    this.setupUser();
    this.loadSchoolFeatures();
    this.loadCourseAssignmentOutcomes();
    this.setupDashboard();

    this.coursePurchasedSubscription = this.dwEventService.getEventData(COURSE_PURCHASED_EVENT, false)
      .subscribe(() => this.refreshAfterCoursePurchase());

    this.integrationRepositoryService?.homePageIntegrationMessage?.subscribe((message) => {
      this.message = message;
    });
  }

  heroLoadComplete() {
    setTimeout(() => { this.heroLoading = false; }, 400);
  }

  onProgramsLoadComplete(rows: any[] | null | undefined): void {
    this.myCoursesCount = Array.isArray(rows) ? rows.length : 0;
    this.tryShowProgramsSection();
  }

  acknowledgeCourseAssignments(): void {
    if (!this.courseAssignmentOutcomes.length || this.acknowledgingCourseAssignments) {
      return;
    }

    this.acknowledgingCourseAssignments = true;
    this.courseAssignmentOutcomeService.acknowledgeCourseAssignments(this.courseAssignmentOutcomes)
      .subscribe({
        next: () => {
          this.courseAssignmentOutcomes = [];
          this.acknowledgingCourseAssignments = false;
        },
        error: () => this.acknowledgingCourseAssignments = false
      });
  }

  onMarketplaceProgramsLoadComplete(rows: any[] | null | undefined): void {
    this.programsCount = Array.isArray(rows) ? rows.length : 0;
    setTimeout(() => { this.programsLoading = false; }, 400);
  }

  onAssignmentsLoadComplete(rows: any[] | null | undefined): void {
    this.assignmentsCount = Array.isArray(rows) ? rows.length : 0;
    setTimeout(() => { this.assignmentsLoading = false; }, 400);
  }

  private setupUser() {
    const user = this.dwSecurityUserService?.securityContext?.ApplicationUser;
    const first = (user as any)?.FirstName;
    if (first) {
      this.firstName = first;
    }
  }

  private loadCourseAssignmentOutcomes(): void {
    this.courseAssignmentOutcomeService.getUnacknowledgedCourseAssignments()
      .subscribe({
        next: outcomes => {
          this.courseAssignmentOutcomes = outcomes || [];
          if (!this.courseAssignmentOutcomes.length) {
            return;
          }

          const courseNames = this.courseAssignmentOutcomes
            .map(outcome => outcome.CourseName)
            .filter(name => !!name);
          this.courseAssignmentAlertTitle = this.courseAssignmentOutcomes.length === 1
            ? 'A new course was assigned to you'
            : `${this.courseAssignmentOutcomes.length} new courses were assigned to you`;
          this.courseAssignmentAlertBody = courseNames.length && courseNames.length <= 3
            ? courseNames.join(', ')
            : 'Your assigned courses are ready in My Courses.';
        },
        error: () => this.courseAssignmentOutcomes = []
      });
  }

  private loadSchoolFeatures(): void {
    this.schoolFeaturesSubscription = this.schoolRepositoryService.watchSchoolFeatures()
      .pipe(mergeMap(schoolFeatures => this.unlimitedCourseAccessService.hasAccess(schoolFeatures)
        .pipe(map(hasAccess => ({ schoolFeatures, hasAccess })))))
      .subscribe({
        next: ({ schoolFeatures, hasAccess }) => {
          const schoolChanged = this.schoolFeaturesFlightSchoolId != null
            && this.schoolFeaturesFlightSchoolId !== schoolFeatures?.FlightSchoolId;
          this.schoolFeaturesFlightSchoolId = schoolFeatures?.FlightSchoolId;

          if (schoolChanged) {
            this.gamificationLoadStarted = false;
            this.gamificationOverviewResolved = false;
            this.dailyQuizResolved = false;
            this.gamificationBootstrap = null;
          }

          this.marketplaceEnabled = !!schoolFeatures?.IsMarketplaceEnabled;
          this.hasUnlimitedCourseAccess = hasAccess;
          this.gamificationEnabled = !!schoolFeatures?.SchoolFeatures?.includes(SchoolFeatureId.Gamification);
          this.schoolFeaturesLoaded = true;
          this.tryShowProgramsSection();
          this.tryShowGamification();
        },
        error: () => {
          this.gamificationEnabled = false;
          this.schoolFeaturesLoaded = true;
          this.tryShowProgramsSection();
          this.tryShowGamification();
        }
      });
  }

  private setupDashboard(refreshPrograms: boolean = false) {
    this.courseRepositoryService.getCurrentStudentCourses()
      .subscribe({
        next: (studentCourses) => {
          this.studentCourses = studentCourses;
          if (refreshPrograms) {
            this.myCoursesCount = studentCourses?.length || 0;
            this.programsLoading = true;
            this.tryShowProgramsSection();
          }
          this.tryShowGamification();
          this.setupCurrentCourse();
          this.heroSlotLoaded = true;

        },
        error: () => {
          this.showGamification = false;
          /* swallow — shell still renders without course data. Mark the
             hero slot resolved so the empty state takes over instead of
             leaving a silent gap above the catalog. */
          this.heroSlotLoaded = true;
          this.streakQuizLoading = false;
          this.achievementsLoading = false;
        }
      });
  }

  private setupCurrentCourse() {
    this.currentStudentCourse = this.studentCourses?.find(
      (sc) => !sc.CourseEndorsementStudent?.CourseEndorsementId
    );

    if (this.currentStudentCourse?.NextCourseLessonId) {
      this.courseProgressFilters = {
        CourseLessonId: this.currentStudentCourse.NextCourseLessonId,
        StudentCourseCourseId: this.currentStudentCourse.CourseId
      };
      this.showProgress = true;
    }
  }

  private tryShowGamification(): void {
    this.showGamification = this.schoolFeaturesLoaded
      && this.gamificationEnabled
      && this.studentCourses?.length > 0;

    if (this.showGamification && !this.gamificationLoadStarted) {
      this.loadGamification();
    }
  }

  private loadGamification(): void {
    this.gamificationLoadStarted = true;
    this.gamificationRepositoryService.getOverview().subscribe({
      next: overview => {
        this.gamificationBootstrap = {
          ...overview,
          Achievements: this.gamificationBootstrap?.Achievements || [],
          ClosestAchievements: this.gamificationBootstrap?.ClosestAchievements || [],
          DailyQuiz: this.gamificationBootstrap?.DailyQuiz
        };
        this.showGamification = overview?.IsAvailable === true;
        this.gamificationOverviewResolved = true;
        this.resolveStreakQuizLoading();
      },
      error: () => {
        this.showGamification = false;
        this.gamificationOverviewResolved = true;
        this.resolveStreakQuizLoading();
      }
    });

    this.gamificationRepositoryService.getAchievements().subscribe({
      next: achievements => {
        const closestAchievements = (achievements || [])
          .filter(achievement => !achievement.Mastered && !!achievement.NextAchievementTierId)
          .sort((left, right) => right.ProgressPercent - left.ProgressPercent || left.DisplayOrder - right.DisplayOrder)
          .slice(0, 3);
        this.gamificationBootstrap = {
          ...this.gamificationBootstrap,
          Achievements: achievements || [],
          ClosestAchievements: closestAchievements,
          CurrentWeek: this.gamificationBootstrap?.CurrentWeek || []
        } as GamificationBootstrap;
        this.achievementsLoading = false;
      },
      error: () => this.achievementsLoading = false
    });

    this.gamificationRepositoryService.getDailyQuizStatus().subscribe({
      next: dailyQuiz => {
        this.gamificationBootstrap = {
          ...this.gamificationBootstrap,
          DailyQuiz: dailyQuiz,
          CurrentWeek: this.gamificationBootstrap?.CurrentWeek || [],
          Achievements: this.gamificationBootstrap?.Achievements || [],
          ClosestAchievements: this.gamificationBootstrap?.ClosestAchievements || []
        } as GamificationBootstrap;
        this.dailyQuizResolved = true;
        this.resolveStreakQuizLoading();
      },
      error: () => {
        this.dailyQuizResolved = true;
        this.resolveStreakQuizLoading();
      }
    });
  }

  ngOnDestroy(): void {
    this.schoolFeaturesSubscription?.unsubscribe();
    this.coursePurchasedSubscription?.unsubscribe();
  }

  private refreshAfterCoursePurchase(): void {
    this.gamificationRepositoryService.invalidate();
    this.gamificationLoadStarted = false;
    this.gamificationOverviewResolved = false;
    this.dailyQuizResolved = false;
    this.gamificationBootstrap = null;
    this.streakQuizLoading = true;
    this.achievementsLoading = true;
    this.setupDashboard(true);
  }

  private resolveStreakQuizLoading(): void {
    this.streakQuizLoading = !this.gamificationOverviewResolved || !this.dailyQuizResolved;
  }

  private tryShowProgramsSection(): void {
    if (this.myCoursesCount === null || !this.schoolFeaturesLoaded) {
      return;
    }

    this.showMarketplacePrograms = this.myCoursesCount === 0
      && (this.marketplaceEnabled || this.hasUnlimitedCourseAccess);
    const showAvailableCourses = this.showMarketplacePrograms
      && !this.marketplaceEnabled
      && this.hasUnlimitedCourseAccess;
    this.availableCoursesTitle = showAvailableCourses ? 'Available Courses' : 'Marketplace';
    this.availableCoursesLinkText = showAvailableCourses ? 'Browse available courses' : 'Browse marketplace';

    if (this.showMarketplacePrograms) {
      return;
    }

    this.programsCount = this.myCoursesCount;
    setTimeout(() => { this.programsLoading = false; }, 400);
  }
}
